/**
 * Global scale state (zustand). Owns the active driver, fans out readings, keeps a short history,
 * tracks stability for the UI and handles BLE auto-reconnect.
 *
 * React components: `const weight = useScale((s) => s.reading?.weightG)`.
 * Non-React consumers (the workflow engine) should use `subscribeReadings` so they see every reading.
 *
 * Ownership: every user action (connect, reconnect, disconnect) first retires whatever is in flight (the
 * reconnect timer, an auto-connect, a connecting driver) and bumps `generation`. Async continuations check
 * the generation after every await and back off when they were superseded, so the status always reflects the
 * latest action and the connect controls can stay usable while connecting or reconnecting.
 *
 * Drivers: the store only knows the registry (registry.ts), the ScaleDriver interface and its optional capabilities
 * (keepAwake, diagnostics, powerOff, beep). connect() opens one picker for every Bluetooth driver and lets the registry
 * choose the driver for the picked device; reconnect() and auto-connect reuse whatever driver that was.
 *
 * Power-off (powerOff()): the store lets go of the driver right after the shutdown command, before the scale drops
 * the link, so the drop is not a lost connection (no auto-reconnect, no error). It then watches that driver privately
 * for a moment to tell whether the scale really switched off, and disconnects it either way.
 */
import { create } from 'zustand';
import type { KeepAwakeMode, RawPacketListener, RawWriteListener, ScaleDriver, ScaleDriverKind, ScaleReading, ScaleStatus, ScaleWarning, StabilityOptions } from './types';
import type { SimulatedScale } from './simulator';
import { connectFailure, isBluetoothSupported as bluetoothSupported, pickerListsAllDevices, requestScaleDevice } from './bluetooth';
import {
  BLUETOOTH_DRIVERS,
  chooseDriver,
  driverChoice,
  driverDescriptor,
  isBluetoothKind,
  lastUsedDriver,
  rememberLastUsedDriver,
  restoreScale,
  type AskDriver,
  type BluetoothDriverChoice,
} from './registry';
import { StabilityDetector } from './stability';
import { thisBrowser, track } from '@/lib/usage';

/** How a connection started, for usage data (shared/usage.ts). */
type ConnectVia = 'picker' | 'reconnect' | 'auto';

/** Usage events for the Bluetooth scales, sent only while sharing is on. The simulator is never counted. */
function trackConnected(kind: ScaleDriverKind, via: ConnectVia): void {
  if (kind !== 'simulator') track('scale_connected', { scale: kind, via, ...thisBrowser() });
}

function trackFailed(kind: ScaleDriverKind | null, via: ConnectVia, error: unknown): void {
  if (kind !== 'simulator') track('scale_connect_failed', { scale: kind ?? 'unknown', via, reason: connectFailure(error), ...thisBrowser() });
}

const HISTORY_MS = 120_000;
/** Quick reconnect attempts after a drop (1, 2, 4, 8, 10 s apart), shown as 'reconnecting'. */
export const MAX_RECONNECT_ATTEMPTS = 5;
/** Auto-connect on app load can't tell whether the scale is switched on, so it gives up quickly. */
export const AUTO_CONNECT_TIMEOUT_MS = 5_000;
/** After the quick attempts, keep retrying quietly (status 'error', controls usable) while the page is visible… */
export const BACKGROUND_RETRY_MS = 30_000;
/** …for this long after the connection was lost. */
export const BACKGROUND_RETRY_FOR_MS = 10 * 60_000;
const BACKGROUND_CONNECT_TIMEOUT_MS = 5_000;

export const LOST_CONNECTION_RETRYING =
  'Lost connection to the scale. Still trying in the background: switch it on, or tap Reconnect.';
export const LOST_CONNECTION = 'Lost connection to the scale. Switch it on, then tap Reconnect.';

/** After the shutdown command, powerOff() waits this long for the link to drop before it disconnects anyway. */
export const POWER_OFF_CONFIRM_MS = 2_500;
/** A weight frame this long (or longer) after the command means the scale ignored it (charging, old firmware). */
export const POWER_OFF_GRACE_MS = 1_500;

/**
 * 'off': the scale switched off (the link dropped or went quiet). 'stillOn': it kept sending weight, so the app
 * disconnected anyway. 'skipped': no connected scale supports it, the command failed, or the user connected again
 * meanwhile; the scale is left as it was.
 */
export type PowerOffResult = 'off' | 'stillOn' | 'skipped';

export interface ScaleState {
  status: ScaleStatus;
  kind: ScaleDriverKind | null;
  deviceName: string | null;
  error: string | null;
  /** The scale is connected but unusable, e.g. every packet fails its checksum. Shown while status is 'connected'. */
  warning: ScaleWarning | null;
  /** The link is nominally up but no data has arrived for a while; status is 'reconnecting' meanwhile. */
  stale: boolean;
  /** A Bluetooth scale from earlier (this session, or permitted via getDevices) that reconnect() reaches without the picker. */
  knownScale: string | null;
  reading: ScaleReading | null;
  batteryPct: number | null;
  stable: boolean;
  /** Mean weight over the stability window while stable, else null. */
  stableWeightG: number | null;
  /** The connected driver can switch the scale off (powerOff). Only meaningful while status is 'connected'. */
  canPowerOff: boolean;
  /**
   * The beep level the connected scale reports (0 = muted, see BeepCapability), from its readings. Null when it reports
   * none, or before its first reading.
   */
  beepLevel: number | null;
  /** The loudest beep level setBeepLevel() takes (the beep capability), or null when the connected scale has none. */
  beepLevels: number | null;
  /**
   * "Which scale is this?" while connecting: the picked device's name matched no driver, and several drivers'
   * services fit or the one that fits was found only by a generic UART service (FFE0, FFF0, Microchip's). Answer with answerDriverQuestion(); disconnect() (Cancel) or any other action cancels it.
   */
  driverQuestion: DriverQuestionState | null;

  /**
   * Opens the Bluetooth device picker for every supported scale (call from a click), then connects with the driver
   * the registry picks for the chosen device. Cancels whatever else is in flight. See ConnectRequest.
   */
  connect(request?: ConnectRequest): Promise<void>;
  /** Reconnect to `knownScale` without the device picker. Cancels whatever else is in flight. */
  reconnect(): Promise<void>;
  /** Try to reconnect to a previously paired BLE scale without a picker (if the browser supports it). */
  tryAutoConnect(): Promise<boolean>;
  /** Disconnect, or cancel a pending connect / reconnect. */
  disconnect(): Promise<void>;
  tare(): Promise<void>;
  startTimer(): Promise<void>;
  stopTimer(): Promise<void>;
  resetTimer(): Promise<void>;
  /**
   * Switch the connected scale off, if its driver can, and end the connection without treating the drop as lost
   * (the next visit offers the usual "Reconnect"). Never throws; see PowerOffResult.
   */
  powerOff(): Promise<PowerOffResult>;
  /** After a checksum warning: accept the scale's frames without checksum verification. */
  acceptUnverifiedPackets(): void;
  /** Set the connected scale's own beep (0 = mute, up to beepLevels). beepLevel follows once the scale reports it. */
  setBeepLevel(level: number): Promise<void>;
  /** The user's answer to driverQuestion (remembered for that device), or null to cancel connecting. */
  answerDriverQuestion(kind: ScaleDriverKind | null): void;
  setStabilityOptions(o: StabilityOptions): void;
  setAutoReconnect(enabled: boolean): void;
  setKeepAwake(mode: KeepAwakeMode): void;
}

export interface ConnectRequest {
  /**
   * A virtual driver (the dev simulator) connects without a picker. A Bluetooth kind limits the picker to that
   * driver and uses it for the chosen device whatever its name.
   */
  kind?: ScaleDriverKind;
  /** List every nearby BLE device in the picker (escape hatch for Bluefy's filter quirks). */
  showAllDevices?: boolean;
}

export interface DriverQuestionState {
  deviceName: string | null;
  options: readonly BluetoothDriverChoice[];
}

// ---------------------------------------------------------------- module-level (non-reactive) state
let driver: ScaleDriver | null = null;
let driverUnsubs: Array<() => void> = [];
/** Last Bluetooth driver bound to a permitted device; kept across disconnects for picker-less reconnects. */
let known: ScaleDriver | null = null;
/** Bumped by every action that takes over the connection; see the file comment. */
let generation = 0;
let autoReconnect = true;
let keepAwakeMode: KeepAwakeMode = 'auto';
let awakeHolds = 0;
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectionLostAt = 0;
/** The driver powerOff() is switching off, from the command until it lets go of the link. */
let poweringOff: ScaleDriver | null = null;
/** Resolves the pending "Which scale is this?" (null = cancelled). */
let answerQuestion: ((kind: ScaleDriverKind | null) => void) | null = null;
/** StrictMode runs effects twice: share one in-flight auto-connect so two drivers never race for the scale. */
let autoConnectInFlight: Promise<boolean> | null = null;
const history: ScaleReading[] = [];
const readingListeners = new Set<(r: ScaleReading) => void>();
const rawListeners = new Set<RawPacketListener>();
const writeListeners = new Set<RawWriteListener>();
const stability = new StabilityDetector({ windowMs: 800, toleranceG: 0.3 });

/** Fields describing live data and the link; reset whenever the link changes. */
const NO_LIVE_DATA = {
  reading: null,
  stable: false,
  stableWeightG: null,
  stale: false,
  warning: null,
  canPowerOff: false,
  beepLevel: null,
  beepLevels: null,
  driverQuestion: null,
} as const;

/** State for a driver whose connection just opened. */
function connectedState(d: ScaleDriver) {
  return { status: 'connected', kind: d.kind, deviceName: d.name, error: null, canPowerOff: d.powerOff != null, beepLevels: d.beep?.levels ?? null } as const;
}

/** Subscribe to every reading from the active driver. Returns unsubscribe. */
export function subscribeReadings(cb: (r: ScaleReading) => void): () => void {
  readingListeners.add(cb);
  return () => readingListeners.delete(cb);
}

/** Subscribe to raw BLE packets (the packet log, the guided brew's recording). */
export function subscribeRawPackets(cb: RawPacketListener): () => void {
  rawListeners.add(cb);
  return () => rawListeners.delete(cb);
}

/** Subscribe to what the active driver writes to the scale, from drivers with onRawWrite. */
export function subscribeRawWrites(cb: RawWriteListener): () => void {
  writeListeners.add(cb);
  return () => writeListeners.delete(cb);
}

/** Readings from the last ~120 s, oldest first. Do not mutate. */
export function getHistory(): readonly ScaleReading[] {
  return history;
}

export function getDriver(): ScaleDriver | null {
  return driver;
}

/** The simulator instance when the simulated scale is active (for the simulator control panel). Dev builds only. */
export function getSimulator(): SimulatedScale | null {
  if (!import.meta.env.DEV) return null;
  return driver?.kind === 'simulator' ? (driver as SimulatedScale) : null;
}

/** Web Bluetooth exists and the page is a secure context (HTTPS or localhost). */
export function isBluetoothSupported(): boolean {
  return bluetoothSupported();
}

/**
 * "Connect scale" already lists every nearby device (Bluefy with several drivers and no scale connected before): no
 * "Show all devices" needed.
 */
export function pickerListsEveryDevice(): boolean {
  return pickerListsAllDevices(BLUETOOTH_DRIVERS, lastUsedDriver());
}

/**
 * Keep the scale from powering off while the app is actively using it (a guided brew, the scale page),
 * as long as the page is visible. Only affects scales that get keep-awake (see KeepAwakeMode).
 * Returns the release function.
 */
export function holdScaleAwake(): () => void {
  awakeHolds++;
  applyAwakeHold();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    awakeHolds--;
    applyAwakeHold();
  };
}

function applyAwakeHold() {
  driver?.keepAwake?.setHold(awakeHolds > 0);
}

function detachDriver() {
  driverUnsubs.forEach((u) => u());
  driverUnsubs = [];
}

/** Cancel everything in flight and let go of the active driver without touching its link. Returns that driver. */
function releaseDriver(): ScaleDriver | null {
  generation++;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  reconnectAttempt = 0;
  const d = driver;
  detachDriver();
  driver = null;
  return d;
}

/**
 * Cancel everything in flight and stop the active driver. Synchronous on purpose: a requestDevice() right
 * after it must still run inside the click's user activation.
 */
function retire(): void {
  cancelDriverQuestion();
  const d = releaseDriver();
  // A scale still being switched off: the user takes over, so close that link first (it may be the same device).
  const p = poweringOff;
  poweringOff = null;
  // Drivers tear down synchronously inside disconnect() (Bluetooth drivers also abort a pending GATT connect).
  if (p && p !== d) void p.disconnect().catch(() => {});
  if (d) void d.disconnect().catch(() => {});
}

/** Ends a pending "Which scale is this?" as cancelled: its connect() was superseded. */
function cancelDriverQuestion(): void {
  const answer = answerQuestion;
  answerQuestion = null;
  answer?.(null);
}

function resetLiveData() {
  history.length = 0;
  stability.reset();
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export const useScale = create<ScaleState>()((set, get) => {
  function handleReading(r: ScaleReading) {
    history.push(r);
    const cutoff = r.t - HISTORY_MS;
    let drop = 0;
    while (drop < history.length && history[drop]!.t < cutoff) drop++;
    if (drop > 0) history.splice(0, drop);

    stability.push(r.t, r.weightG);
    set({
      reading: r,
      batteryPct: r.batteryPct ?? get().batteryPct,
      beepLevel: r.beepLevel ?? get().beepLevel,
      stable: stability.stable,
      stableWeightG: stability.stableWeight,
    });
    readingListeners.forEach((cb) => {
      try {
        cb(r);
      } catch (e) {
        console.error('reading listener failed', e);
      }
    });
  }

  function attach(d: ScaleDriver) {
    detachDriver();
    driver = d;
    d.keepAwake?.setMode(keepAwakeMode);
    d.keepAwake?.setHold(awakeHolds > 0);
    driverUnsubs.push(d.onReading(handleReading));
    driverUnsubs.push(d.onDisconnect((reason) => handleDisconnect(d, reason)));
    if (d.onRawPacket) driverUnsubs.push(d.onRawPacket((b, t) => rawListeners.forEach((cb) => cb(b, t))));
    if (d.onRawWrite) driverUnsubs.push(d.onRawWrite((b, label, t, ok) => writeListeners.forEach((cb) => cb(b, label, t, ok))));
    if (d.onStale) driverUnsubs.push(d.onStale((stale) => handleStale(d, stale)));
    if (d.onWarning) driverUnsubs.push(d.onWarning((w) => handleWarning(d, w)));
  }

  function rememberScale(d: ScaleDriver) {
    known = d;
    set({ knownScale: d.name });
  }

  function handleDisconnect(d: ScaleDriver, reason?: string) {
    // While powerOff() sends the command, a drop is the scale switching off: powerOff() takes it from there.
    if (driver !== d || poweringOff === d) return;
    if (d.kind !== 'simulator') track('scale_dropped', { scale: d.kind, ...thisBrowser() });
    if (!isBluetoothKind(d.kind) || !autoReconnect) {
      generation++;
      detachDriver();
      driver = null;
      set({ status: 'disconnected', kind: null, error: reason ?? null, ...NO_LIVE_DATA });
      return;
    }
    connectionLostAt = performance.now();
    reconnectAttempt = 0;
    scheduleReconnect(d, ++generation);
  }

  function scheduleReconnect(d: ScaleDriver, gen: number) {
    const background = reconnectAttempt >= MAX_RECONNECT_ATTEMPTS;
    if (background && performance.now() - connectionLostAt >= BACKGROUND_RETRY_FOR_MS) {
      retire();
      set({ status: 'error', kind: null, error: LOST_CONNECTION, ...NO_LIVE_DATA });
      return;
    }
    const delay = background ? BACKGROUND_RETRY_MS : Math.min(1000 * 2 ** reconnectAttempt, 10_000);
    reconnectAttempt++;
    // Drop the last reading so no stale weight is shown (or acted on) while the link is down. In the
    // background phase the controls offer Reconnect / Choose scale (and Simulator in dev), all of which cancel this loop.
    set(background ? { status: 'error', error: LOST_CONNECTION_RETRYING, ...NO_LIVE_DATA } : { status: 'reconnecting', error: null, ...NO_LIVE_DATA });
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => void attemptReconnect(d, gen, background), delay);
  }

  async function attemptReconnect(d: ScaleDriver, gen: number, background: boolean) {
    reconnectTimer = null;
    if (gen !== generation || driver !== d) return;
    if (background && !pageVisible()) {
      scheduleReconnect(d, gen);
      return;
    }
    try {
      await d.connect(background ? { timeoutMs: BACKGROUND_CONNECT_TIMEOUT_MS } : undefined);
    } catch {
      // Cancelled by disconnect() / connect() / reconnect(): they own the status now.
      if (gen !== generation || driver !== d) return;
      scheduleReconnect(d, gen);
      return;
    }
    if (gen !== generation || driver !== d) {
      if (driver !== d) void d.disconnect().catch(() => {});
      return;
    }
    reconnectAttempt = 0;
    set(connectedState(d));
  }

  function handleStale(d: ScaleDriver, stale: boolean) {
    if (driver !== d) return;
    if (stale) {
      if (get().status !== 'connected') return;
      stability.reset();
      set({ status: 'reconnecting', stale: true, reading: null, stable: false, stableWeightG: null });
    } else if (get().stale) {
      set({ status: 'connected', stale: false });
    }
  }

  function handleWarning(d: ScaleDriver, warning: ScaleWarning | null) {
    if (driver !== d) return;
    if (!warning) {
      set({ warning: null });
      return;
    }
    // Nothing valid is arriving: don't keep showing (or acting on) the last good weight.
    stability.reset();
    set({ warning, reading: null, stable: false, stableWeightG: null });
  }

  async function command(fn: (d: ScaleDriver) => Promise<void>) {
    if (!driver || !driver.connected) throw new Error('Scale not connected');
    await fn(driver);
  }

  return {
    status: 'disconnected',
    kind: null,
    deviceName: null,
    error: null,
    warning: null,
    stale: false,
    knownScale: null,
    reading: null,
    batteryPct: null,
    stable: false,
    stableWeightG: null,
    canPowerOff: false,
    beepLevel: null,
    beepLevels: null,
    driverQuestion: null,

    async connect(request = {}) {
      retire();
      const gen = generation;
      resetLiveData();
      // Only registered kinds: the simulator is registered in dev builds only.
      const forced = request.kind ? driverDescriptor(request.kind) : undefined;
      set({ status: 'connecting', kind: forced?.kind ?? null, error: null, deviceName: null, ...NO_LIVE_DATA });
      let d: ScaleDriver | null = null;
      try {
        if (request.kind && !forced) throw new Error(`No "${request.kind}" scale driver in this build.`);
        if (forced?.transport === 'virtual') {
          d = forced.create();
        } else {
          const offered = forced ? [forced] : BLUETOOTH_DRIVERS;
          // Synchronous up to requestDevice(): the picker needs the click's user activation.
          const device = await requestScaleDevice(offered, { showAllDevices: request.showAllDevices, preferred: forced ? undefined : lastUsedDriver(offered) });
          if (gen !== generation) return;
          const ask: AskDriver = (question) =>
            new Promise((resolve) => {
              if (gen !== generation) return resolve(null);
              cancelDriverQuestion();
              answerQuestion = (kind) => resolve(question.candidates.find((c) => c.kind === kind) ?? null);
              set({ driverQuestion: { deviceName: question.deviceName, options: question.candidates.map(driverChoice) } });
            });
          const chosen = forced ? { descriptor: forced, probed: false } : await chooseDriver(device, offered, { ask });
          if (gen !== generation) {
            if (chosen.probed) device.gatt?.disconnect();
            return;
          }
          d = chosen.descriptor.create(device);
          set({ kind: d.kind });
        }
        attach(d);
        await d.connect();
      } catch (e) {
        if (gen !== generation) return;
        detachDriver();
        driver = null;
        const msg = errorMessage(e);
        // User closing the BLE chooser is not an error worth shouting about.
        const cancelled = /cancel|chooser|user gesture/i.test(msg) && !/gesture required/i.test(msg);
        set({ status: cancelled ? 'disconnected' : 'error', error: cancelled ? null : msg, kind: null });
        if (!cancelled) {
          trackFailed(d?.kind ?? forced?.kind ?? null, 'picker', e);
          throw e;
        }
        return;
      }
      if (gen !== generation) {
        if (driver !== d) void d.disconnect().catch(() => {});
        return;
      }
      if (isBluetoothKind(d.kind)) {
        rememberScale(d);
        rememberLastUsedDriver(d.kind);
      }
      set(connectedState(d));
      trackConnected(d.kind, 'picker');
    },

    async reconnect() {
      const d = known;
      if (!d) throw new Error('No scale to reconnect to. Tap “Connect scale” to choose one.');
      if (driver === d && d.connected && get().status === 'connected') return;
      retire();
      const gen = generation;
      resetLiveData();
      set({ status: 'connecting', kind: d.kind, deviceName: d.name, error: null, ...NO_LIVE_DATA });
      attach(d);
      try {
        // Same BluetoothDevice: no picker, no user gesture needed.
        await d.connect();
      } catch (e) {
        if (gen !== generation) return;
        detachDriver();
        driver = null;
        set({ status: 'error', kind: null, error: errorMessage(e) });
        trackFailed(d.kind, 'reconnect', e);
        throw e;
      }
      if (gen !== generation) {
        if (driver !== d) void d.disconnect().catch(() => {});
        return;
      }
      set(connectedState(d));
      trackConnected(d.kind, 'reconnect');
    },

    tryAutoConnect() {
      if (autoConnectInFlight) return autoConnectInFlight;
      autoConnectInFlight = (async () => {
        // Something is active, or the user already picked a scale this session: nothing to restore.
        if (driver || known || answerQuestion) return driver?.connected ?? false;
        const gen = generation;
        const restored = await restoreScale().catch(() => null);
        if (!restored) return false;
        // The user acted while restore() was pending. (getDriver(): TS keeps `driver` narrowed to null from
        // the guard above, but connect() may have run meanwhile.)
        const current = getDriver();
        if (gen !== generation || current || known || answerQuestion) return current?.connected ?? false;
        // Remember it even if this attempt fails (scale switched off): "Reconnect" then needs no picker.
        rememberScale(restored);
        const own = ++generation;
        resetLiveData();
        set({ status: 'connecting', kind: restored.kind, deviceName: restored.name, error: null, ...NO_LIVE_DATA });
        attach(restored);
        try {
          await restored.connect({ timeoutMs: AUTO_CONNECT_TIMEOUT_MS });
        } catch {
          if (own === generation) {
            detachDriver();
            driver = null;
            set({ status: 'disconnected', kind: null });
          }
          return false;
        }
        if (own !== generation) {
          if (driver !== restored) void restored.disconnect().catch(() => {});
          return false;
        }
        set(connectedState(restored));
        // Not its failures: a scale that's switched off at start is the usual case, not a problem.
        trackConnected(restored.kind, 'auto');
        return true;
      })().finally(() => {
        autoConnectInFlight = null;
      });
      return autoConnectInFlight;
    },

    async disconnect() {
      retire();
      set({ status: 'disconnected', kind: null, deviceName: null, error: null, ...NO_LIVE_DATA });
    },

    tare: () => command((d) => d.tare()),
    startTimer: () => command((d) => d.startTimer()),
    stopTimer: () => command((d) => d.stopTimer()),
    resetTimer: () => command((d) => d.resetTimer()),

    async powerOff() {
      const d = driver;
      if (!d || d.powerOff == null || poweringOff || !d.connected || get().status !== 'connected') return 'skipped';
      let dropped = false;
      let lastReadingAt = Number.NEGATIVE_INFINITY;
      let wake: (() => void) | null = null;
      const unwatch = [
        d.onDisconnect(() => {
          dropped = true;
          wake?.();
        }),
        d.onReading(() => {
          lastReadingAt = performance.now();
        }),
      ];
      const finish = (result: PowerOffResult): PowerOffResult => {
        unwatch.forEach((u) => u());
        if (poweringOff === d) poweringOff = null;
        return result;
      };

      poweringOff = d;
      try {
        await d.powerOff();
      } catch {
        // The link going down with the command means the scale switched off. Anything else: leave it connected.
        if (poweringOff !== d || (!dropped && d.connected)) return finish('skipped');
      }
      // The user connected, reconnected or disconnected meanwhile: they own the scale now.
      if (poweringOff !== d || driver !== d) return finish('skipped');

      // Let go before the scale drops the link, so the drop isn't a lost connection.
      releaseDriver();
      set({ status: 'disconnected', kind: null, deviceName: null, error: null, ...NO_LIVE_DATA });

      const sentAt = performance.now();
      if (!dropped) {
        await new Promise<void>((resolve) => {
          const timer = setTimeout(() => {
            wake = null;
            resolve();
          }, POWER_OFF_CONFIRM_MS);
          wake = () => {
            clearTimeout(timer);
            wake = null;
            resolve();
          };
        });
      }
      // retire() (a new connect or reconnect) already closed this link.
      if (poweringOff !== d) return finish('skipped');
      // The link may stay up for a few seconds after a real power-off (supervision timeout), but frames stop.
      const stillOn = !dropped && lastReadingAt >= sentAt + POWER_OFF_GRACE_MS;
      const result = finish(stillOn ? 'stillOn' : 'off');
      void d.disconnect().catch(() => {});
      return result;
    },

    acceptUnverifiedPackets() {
      driver?.diagnostics?.acceptUnverifiedPackets?.();
    },

    setBeepLevel: (level) =>
      command((d) => {
        if (!d.beep) throw new Error('This scale’s beep can’t be set from Taretto.');
        return d.beep.setLevel(level);
      }),

    answerDriverQuestion(kind) {
      const answer = answerQuestion;
      answerQuestion = null;
      set({ driverQuestion: null });
      answer?.(kind);
    },

    setStabilityOptions(o) {
      stability.configure(o);
    },
    setAutoReconnect(enabled) {
      autoReconnect = enabled;
    },
    setKeepAwake(mode) {
      keepAwakeMode = mode;
      driver?.keepAwake?.setMode(mode);
      if (known && known !== driver) known.keepAwake?.setMode(mode);
    },
  };
});
